import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { rulesAPI } from '../services/api';

interface Rule {
  id: number;
  name: string;
  accountCount: number;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState<Rule[]>([]);

  // 그룹 활성 체크
  const isMddActive =
    location.pathname.startsWith('/accounts') ||
    location.pathname.startsWith('/a-book') ||
    location.pathname.startsWith('/rules') ||
    location.pathname.startsWith('/statistics');
  const isPointActive = location.pathname.startsWith('/point-rule');
  const isPatternActive = location.pathname.startsWith('/pattern');

  // 한 번에 하나의 그룹만 열림 (아코디언)
  type OpenGroup = 'mdd' | 'point' | 'pattern' | null;
  const getInitialGroup = (): OpenGroup => {
    if (isMddActive) return 'mdd';
    if (isPointActive) return 'point';
    if (isPatternActive) return 'pattern';
    return null;
  };
  const [openGroup, setOpenGroup] = useState<OpenGroup>(getInitialGroup());

  useEffect(() => {
    rulesAPI.getAll().then(res => setRules(res.data)).catch(() => {});
  }, []);

  // 현재 경로 바뀌면 해당 그룹 자동 열림
  useEffect(() => {
    if (isMddActive) setOpenGroup('mdd');
    else if (isPointActive) setOpenGroup('point');
    else if (isPatternActive) setOpenGroup('pattern');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (g: OpenGroup) => {
    setOpenGroup(prev => prev === g ? null : g);
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = (path: string) => location.pathname === path;

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logo}>AB</div>
          <div>
            <div style={styles.logoTitle}>AB Book</div>
            <div style={styles.logoSub}>Hybrid System</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {/* 대시보드 */}
          <button
            style={isActive('/dashboard') ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem}
            onClick={() => navigate('/dashboard')}
          >
            <span style={styles.navIcon}>📊</span>
            <span>대시보드</span>
          </button>

          {/* ━━━━━ MDD 룰 시스템 ━━━━━ */}
          <div>
            <button
              style={{ ...styles.navItem, ...(isMddActive ? styles.navItemActive : {}) }}
              onClick={() => toggleGroup('mdd')}
            >
              <span style={styles.navIcon}>⚙️</span>
              <span style={{ flex: 1 }}>MDD 룰 시스템</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{openGroup === 'mdd' ? '▲' : '▼'}</span>
            </button>
            {openGroup === 'mdd' && (
              <div style={styles.subMenu}>
                <button
                  style={location.pathname.startsWith('/accounts')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/accounts')}
                >
                  <span style={{ marginRight: '6px' }}>📋</span>
                  <span style={{ flex: 1 }}>계좌 관리</span>
                </button>
                <button
                  style={location.pathname.startsWith('/a-book')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/a-book')}
                >
                  <span style={{ marginRight: '6px' }}>📖</span>
                  <span style={{ flex: 1 }}>A북 현황</span>
                </button>
                <button
                  style={isActive('/rules')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/rules')}
                >
                  <span style={{ marginRight: '6px' }}>⚙️</span>
                  <span style={{ flex: 1 }}>룰 관리</span>
                </button>
                <button
                  style={isActive('/statistics/rules')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/statistics/rules')}
                >
                  <span style={{ marginRight: '6px' }}>📊</span>
                  <span style={{ flex: 1 }}>룰별 수익</span>
                </button>
                <button
                  style={isActive('/statistics/accounts')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/statistics/accounts')}
                >
                  <span style={{ marginRight: '6px' }}>👤</span>
                  <span style={{ flex: 1 }}>전체 계좌 손익</span>
                </button>
              </div>
            )}
          </div>

          {/* ━━━━━ 포인트 룰 시스템 ━━━━━ */}
          <div>
            <button
              style={{ ...styles.navItem, ...(isPointActive ? styles.navItemActive : {}) }}
              onClick={() => toggleGroup('point')}
            >
              <span style={styles.navIcon}>📍</span>
              <span style={{ flex: 1 }}>포인트 룰 시스템</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{openGroup === 'point' ? '▲' : '▼'}</span>
            </button>
            {openGroup === 'point' && (
              <div style={styles.subMenu}>
                <button
                  style={location.pathname === '/point-rule' || location.pathname === '/point-rule/rules'
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/point-rule/rules')}
                >
                  <span style={{ marginRight: '6px' }}>⚙️</span>
                  <span style={{ flex: 1 }}>룰 관리</span>
                </button>
                <button
                  style={isActive('/point-rule/symbols')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/point-rule/symbols')}
                >
                  <span style={{ marginRight: '6px' }}>💱</span>
                  <span style={{ flex: 1 }}>심볼 설정</span>
                </button>
                <button
                  style={isActive('/point-rule/accounts')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/point-rule/accounts')}
                >
                  <span style={{ marginRight: '6px' }}>📋</span>
                  <span style={{ flex: 1 }}>전환 포인트 계좌</span>
                </button>
                <button
                  style={isActive('/point-rule/statistics')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/point-rule/statistics')}
                >
                  <span style={{ marginRight: '6px' }}>📊</span>
                  <span style={{ flex: 1 }}>통계</span>
                </button>
                <button
                  style={isActive('/point-rule/reconciliation')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/point-rule/reconciliation')}
                >
                  <span style={{ marginRight: '6px' }}>🔍</span>
                  <span style={{ flex: 1 }}>적합성 검증</span>
                </button>
              </div>
            )}
          </div>

          {/* ━━━━━ 패턴 분석 ━━━━━ */}
          <div>
            <button
              style={{ ...styles.navItem, ...(isPatternActive ? styles.navItemActive : {}) }}
              onClick={() => toggleGroup('pattern')}
            >
              <span style={styles.navIcon}>🧠</span>
              <span style={{ flex: 1 }}>패턴 분석</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{openGroup === 'pattern' ? '▲' : '▼'}</span>
            </button>
            {openGroup === 'pattern' && (
              <div style={styles.subMenu}>
                {[
                  { path: '/pattern/short-high-winrate', label: '단기 고승률형', icon: '⚡' },
                  { path: '/pattern/long-stable', label: '장기 안정수익형', icon: '📈' },
                  { path: '/pattern/high-payoff', label: '고Payoff형', icon: '🎯' },
                  { path: '/pattern/normal-loss', label: '일반 손실형', icon: '📉' },
                  { path: '/pattern/recommendation', label: 'Monitor DB 추천', icon: '🤖' },
                ].map(child => (
                  <button
                    key={child.path}
                    style={isActive(child.path) ? { ...styles.subMenuItem, ...styles.subMenuItemActive } : styles.subMenuItem}
                    onClick={() => navigate(child.path)}
                  >
                    <span style={{ marginRight: '6px' }}>{child.icon}</span>
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.userAvatar}>{user?.name?.[0] || 'A'}</div>
            <div>
              <div style={styles.userName}>{user?.name || 'Admin'}</div>
              <div style={styles.userRole}>{user?.role || 'SUPER'}</div>
            </div>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>로그아웃</button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.content}>{children}</div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', minHeight: '100vh', background: '#0f0f1a', fontFamily: "'Pretendard', -apple-system, sans-serif" },
  sidebar: { width: '220px', minHeight: '100vh', background: 'rgba(255,255,255,0.03)', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', padding: '24px 16px', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100, overflowY: 'auto' },
  sidebarHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '36px', padding: '0 8px' },
  logo: { width: '40px', height: '40px', background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: '#fff', flexShrink: 0 },
  logoTitle: { color: '#fff', fontSize: '16px', fontWeight: 700 },
  logoSub: { color: 'rgba(255,255,255,0.4)', fontSize: '11px' },
  nav: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%' },
  navItemActive: { background: 'rgba(102,126,234,0.2)', color: '#667eea', fontWeight: 600 },
  navIcon: { fontSize: '16px', width: '20px', textAlign: 'center' },
  subMenu: { marginLeft: '16px', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '2px' },
  subMenuItem: { display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', textAlign: 'left', width: '100%' },
  subMenuItemActive: { background: 'rgba(102,126,234,0.15)', color: '#a78bfa', fontWeight: 600 },
  countBadge: { background: 'rgba(102,126,234,0.3)', color: '#a78bfa', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: 700 },
  sidebarFooter: { borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px', marginTop: '16px' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '0 4px' },
  userAvatar: { width: '32px', height: '32px', background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 700, flexShrink: 0 },
  userName: { color: '#fff', fontSize: '13px', fontWeight: 600 },
  userRole: { color: 'rgba(255,255,255,0.4)', fontSize: '11px' },
  logoutBtn: { width: '100%', padding: '8px', background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '8px', color: '#ff6b6b', fontSize: '13px', cursor: 'pointer' },
  main: { marginLeft: '220px', flex: 1, minHeight: '100vh', overflow: 'auto' },
  content: { padding: '32px', width: '100%', boxSizing: 'border-box' as const },
};

export default Layout;
